import type { MedicationEntry } from '@/types';
import type { ClinicalFlag } from '@/lib/clinical-dictionaries';
import { flagsForMedication } from '@/lib/pt-history-dictionaries';
import { AlertTriangle, Droplet, Syringe, Pill } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * MedicationAlerts — decision-support banner stack.
 *
 * Scans the patient's current-medications list for clinically
 * significant tags and renders a compact row of red/amber alert
 * banners. The highest-priority alert is the ANTICOAGULANT /
 * ANTIPLATELET one — per the clinical requirement, any non-aspirin
 * blood thinner triggers a prominent red banner so the provider is
 * reminded before any invasive procedure, during bleeding assessment,
 * and when considering reversal (PCC / Andexxa / idarucizumab / FFP).
 *
 * Other tagged classes (insulin, opioid, beta-blocker,
 * immunosuppressant) get lower-profile amber banners — still
 * visible, but not screaming red.
 */

interface Props {
  medications: MedicationEntry[];
  /** Compact variant — tighter padding + no descriptions. Default false. */
  compact?: boolean;
}

interface AlertDef {
  flag: ClinicalFlag;
  tone: 'red' | 'amber';
  icon: LucideIcon;
  heading: string;
  prompt: string;
}

// Ordered by severity — the UI renders them in this order.
const ALERTS: AlertDef[] = [
  {
    flag: 'anticoagulant',
    tone: 'red',
    icon: Droplet,
    heading: 'Anticoagulant',
    prompt:
      'Bleeding risk. Consider reversal if active hemorrhage (PCC / Andexxa / Praxbind / FFP per agent). Document INR if on warfarin.',
  },
  {
    flag: 'antiplatelet',
    tone: 'red',
    icon: Droplet,
    heading: 'Antiplatelet (non-aspirin)',
    prompt:
      'Platelet dysfunction — bleeding risk on top of trauma / invasive procedures. No effective field reversal.',
  },
  {
    flag: 'insulin',
    tone: 'amber',
    icon: Syringe,
    heading: 'Insulin Therapy',
    prompt: 'Check blood glucose on any ALOC / dizziness / diaphoresis presentation.',
  },
  {
    flag: 'beta_blocker',
    tone: 'amber',
    icon: Pill,
    heading: 'Beta Blocker',
    prompt:
      'Masks tachycardic response to shock and hypoglycemia. Reassess vitals over time; don\'t rule out shock on HR alone.',
  },
  {
    flag: 'opioid',
    tone: 'amber',
    icon: Pill,
    heading: 'Chronic Opioid',
    prompt: 'Respiratory depression risk with additional sedation. Naloxone-ready.',
  },
  {
    flag: 'immunosuppressant',
    tone: 'amber',
    icon: Pill,
    heading: 'Immunosuppressed',
    prompt: 'Lower fever threshold for sepsis. Atypical infection presentations.',
  },
];

export function MedicationAlerts({ medications, compact = false }: Props) {
  // Resolve each med's flags (either stored on the record or derived from the dictionary)
  const active = new Set<ClinicalFlag>();
  for (const m of medications) {
    const persisted = m.clinical_flags ?? [];
    const derived = flagsForMedication(m.name);
    for (const f of [...persisted, ...derived] as ClinicalFlag[]) active.add(f);
  }

  const firing = ALERTS.filter((a) => active.has(a.flag));
  if (firing.length === 0) return null;

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {firing.map((a) => (
        <AlertBanner key={a.flag} def={a} compact={compact} />
      ))}
    </div>
  );
}

function AlertBanner({ def, compact }: { def: AlertDef; compact: boolean }) {
  const red = def.tone === 'red';
  // High-contrast: use saturated fill + near-white text + thick left rail
  // + bold heading. Previous text-red-200 on bg-red-950/60 was WCAG
  // borderline at best; the new combo reads at >7:1 contrast.
  const cls = red
    ? 'border-red-400 border-l-red-500 bg-red-600/90 text-white'
    : 'border-amber-400 border-l-amber-500 bg-amber-600/85 text-white';
  return (
    <div className={`flex items-start gap-2 border-l-4 border ${cls} px-2.5 ${compact ? 'py-1' : 'py-1.5'} shadow-sm`}>
      <def.icon size={14} className="text-white shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] font-black uppercase tracking-widest">
            {red ? 'ALERT' : 'NOTE'}
          </span>
          <span className="text-sm font-bold">{def.heading}</span>
        </div>
        {!compact && (
          <div className="text-[11px] leading-snug mt-0.5 text-white/95">{def.prompt}</div>
        )}
      </div>
      {red && <AlertTriangle size={14} className="text-white shrink-0 mt-0.5" />}
    </div>
  );
}
